import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';
import { createPrismaDateColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/DateColumn.tsx';
import { createTextColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/TextColumn.tsx';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting.ts';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns.tsx';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n.ts';
import { SecurityType } from '@dealstack/shared/lib/generated';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import { ActionIcon, Flex, Text } from '@mantine/core';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';
import { SecurityFormData } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/Securities/types.ts';
import { VestingToggleCell } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/Securities/components/SecuritiesTable/VestingToggleCell.tsx';

type Handlers = {
	onEdit: (security: SecurityFormData) => void;
	onToggleVesting: (security: SecurityFormData) => void;
};

interface Props {
	tMipManage: TranslateFunction<I18Namespaces.MIP_MANAGE>;
	getSecurityTypeTranslation: (type: SecurityType) => string;
	handlers: Handlers;
}

export const getSecuritiesTableColumns = ({
	tMipManage,
	getSecurityTypeTranslation,
	handlers,
}: Props): ExtendedColumnDef<SecurityFormData>[] => {
	const { onEdit, onToggleVesting } = handlers;

	return [
		createTextColumn({
			id: 'type',
			headerText: tMipManage('root.securities.table.header.type'),
			filterText: tMipManage('root.securities.table.filter.type'),
			columnLabel: tMipManage('root.securities.table.header.type'),
			accessorFn: (row) => getSecurityTypeTranslation(row.type),
			enableGlobalFilter: false,
			showFilter: false,
		}),
		createTextColumn({
			id: 'name',
			headerText: tMipManage('root.securities.table.header.name'),
			filterText: tMipManage('root.securities.table.filter.name'),
			columnLabel: tMipManage('root.securities.table.header.name'),
			accessorKey: 'name',
			enableGlobalFilter: false,
			showFilter: false,
			cell: ({ getValue }) => {
				const value = getValue() as string;
				return value || LONG_DASH;
			},
		}),
		{
			id: 'isSubjectToVesting',
			header: () => (
				<Text fw={600}>
					{tMipManage('root.securities.table.header.subject_to_vesting')}
				</Text>
			),
			accessorFn: (row) => row.isSubjectToVesting,
			enableSorting: false,
			enableColumnFilter: false,
			size: 140,
			cell: ({ row }) => {
				const security = row.original;
				return <VestingToggleCell security={security} onToggle={onToggleVesting} />;
			},
		},
		createTextColumn({
			id: 'fairMarketValuePerUnit',
			headerText: tMipManage('root.securities.table.header.value_per_share'),
			filterText: tMipManage('root.securities.table.filter.value_per_share'),
			columnLabel: tMipManage('root.securities.table.header.value_per_share'),
			accessorKey: 'fairMarketValuePerUnit',
			enableGlobalFilter: false,
			showFilter: false,
			cell: ({ getValue }) => {
				const value = getValue() as number | null | undefined;
				return (
					<Text ta="center">
						{value !== null && value !== undefined ? value : LONG_DASH}
					</Text>
				);
			},
		}),
		{
			id: 'actions',
			header: '',
			enableSorting: false,
			enableColumnFilter: false,
			size: 80,
			cell: ({ row }) => (
				<Flex justify="flex-end" w="100%">
					<ActionIcon
						data-testid={TEST_ELEMENT_IDS.MIP_PROGRAMME_SECURITY_EDIT_BUTTON}
						onClick={() => onEdit(row.original)}
						size="lg"
					>
						<Icon variant="pencil" size="lg" color="black" />
					</ActionIcon>
				</Flex>
			),
		},
		createPrismaDateColumn<SecurityFormData>({
			headerText: tMipManage('securities.table.columns.created'),
			columnLabel: tMipManage('securities.table.columns.created'),
			filterText: tMipManage('securities.table.filter.created'),
			accessorKey: 'createdAt',
			enableColumnFilter: false,
		}),
	];
};
